"use client";

import { createContext, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_LANGUAGE,
  LOCALE_COOKIE_NAME,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "../constants";

const LANGUAGE_LABELS: Partial<Record<SupportedLanguage, string>> = {
  en: "English",
  es: "Español (Spanish)",
  zh: "中文 (Chinese)",
  ko: "한국어 (Korean)",
};

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  languages: Partial<Record<SupportedLanguage, string>>;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1];
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
}

interface LanguageProviderProps {
  children: React.ReactNode;
  initialLanguage?: SupportedLanguage;
}

export function LanguageProvider({
  children,
  initialLanguage,
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    initialLanguage ?? DEFAULT_LANGUAGE,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on mount to sync cookie → state
  useEffect(() => {
    const saved = getCookie(LOCALE_COOKIE_NAME);
    if (
      saved &&
      SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage) &&
      saved !== language
    ) {
      setLanguageState(saved as SupportedLanguage);
    }
  }, []);

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    setCookie(LOCALE_COOKIE_NAME, lang);
  }, []);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, languages: LANGUAGE_LABELS }}
    >
      {children}
    </LanguageContext.Provider>
  );
}
